import { Divider, Heading } from '@sparemin/blockhead';
import { noop, over } from 'lodash-es';
import React, { useCallback } from 'react';
import { FieldErrors } from 'react-hook-form';
import useToggle from 'hooks/useToggle/useToggle';
import { useAuth } from 'ui/context';
import useGoogleOneTapAuth from 'ui/hooks/useGoogleOneTapAuth';
import {
  AuthenticationProvider,
  AuthSuccessResponse,
  SocialAuthSuccessResponse,
  UserAuthResult,
} from 'ui/types';
import { createUserAuthResult } from 'ui/utils';
import AuthCard from '../AuthCard';
import AuthNavLink from '../AuthNavLink';
import AuthSpacer from '../AuthSpacer';
import GoogleSignInButton from '../GoogleSignInButton';
import SignUpForm from '../SignUpForm';
import { SignUpFormData } from '../SignUpForm/useSignUpForm';
import { SIGN_UP_CARD_TITLE } from './constants';

export type RegistrationProvider = Extract<
  AuthenticationProvider,
  'google-one-tap' | 'google-sign-in' | 'headliner'
>;

export interface SignUpCardProps {
  /**
   * Callback when there is an error.
   *
   * `errors.unregisteredError` is an error that doesn't necessarily correspond
   * to a single input field (e.g. general API error)
   * 
   * When there is a validation error submitting the form, `errors` will be of type
   * `FieldErrors<SignUpFormData>`.  When there is an error that occurs after the form
   * has been validated (for example, a failed API request), then the `errors` will
   * be of type `Error`

   */
  onError?: (errors: FieldErrors<SignUpFormData> | Error) => void;
  /**
   * Called when authentication is successful.  The inputs will remain disabled until
   * the promise returned by this function resolves.
   */
  onAuthenticateSuccess?: (
    provider: RegistrationProvider,
    data: UserAuthResult,
  ) => Promise<void>;
  /**
   * Called when the registration form is submitted or when a social auth
   * button is clicked.  This is an "onPress" event that fires when the user
   * intreacts with the UI
   */
  onSubmit?: (
    authProvider: Exclude<AuthenticationProvider, 'google-one-tap'>,
  ) => void;
}

/**
 * A `Card` that handles user registration
 */
export const SignUpCard: React.FC<SignUpCardProps> = ({
  onAuthenticateSuccess,
  onError = noop,
  onSubmit,
}) => {
  const {
    value: loading,
    toggleOn: startLoading,
    toggleOff: stopLoading,
  } = useToggle();

  const { features } = useAuth();

  const createHandleAuthenticateSuccess =
    (provider: RegistrationProvider) =>
    async (res: AuthSuccessResponse | SocialAuthSuccessResponse) => {
      try {
        // if the auth form was used, event type is "registration", otherwise it is left
        // as undefined so that createUserAuthResult can determine whether or not the
        // user was created
        const authEventType =
          provider === 'headliner' ? 'registration' : undefined;

        await onAuthenticateSuccess?.(
          provider,
          createUserAuthResult(res, authEventType),
        );
      } finally {
        stopLoading();
      }
    };

  useGoogleOneTapAuth({
    onSuccess: createHandleAuthenticateSuccess('google-one-tap'),
  });

  const handleFormSubmit = useCallback(() => {
    onSubmit?.('headliner');
  }, [onSubmit]);

  const handleGoogleSubmit = useCallback(() => {
    onSubmit?.('google-sign-in');
  }, [onSubmit]);

  const supportsLogin = features.includes('login');

  return (
    <AuthCard
      footer={
        !supportsLogin ? null : (
          <AuthCard.Footer>
            <Heading level={2}>
              Already have an account?{' '}
              <AuthNavLink appearance="link" to="login" variant="inherit">
                Log in here
              </AuthNavLink>
            </Heading>
          </AuthCard.Footer>
        )
      }
      header={<AuthCard.Header title={SIGN_UP_CARD_TITLE} />}
    >
      <AuthCard.Contents as={AuthSpacer} align="start">
        {/* TODO need default error message and some way to display errors (probably within the form) */}
        <GoogleSignInButton
          isDisabled={loading}
          onCancel={stopLoading}
          onError={stopLoading}
          onPress={over([handleGoogleSubmit, startLoading])}
          onSuccess={createHandleAuthenticateSuccess('google-sign-in')}
        />
        <Divider space="0px">OR</Divider>
        <SignUpForm
          isDisabled={loading}
          onAuthenticateSuccess={createHandleAuthenticateSuccess('headliner')}
          onError={over([onError, stopLoading])}
          onSubmit={over([handleFormSubmit, startLoading])}
        />
      </AuthCard.Contents>
    </AuthCard>
  );
};

export default SignUpCard;
